import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { QualityScorePanel } from '../components/QualityScorePanel'
import { DidAttributionPanel } from '../components/DidAttributionPanel'
import { TimestampRow } from '../components/Hint'
import { TransmissionMeasurements } from '../components/TransmissionMeasurements'
import { outboundHandleLabel } from '../utils/outboundHandle'
import { theme } from '../theme'

/**
 * Per-fax detail for one item in a batch.
 *
 * A separate page from run detail because a batch item is not in `runs` — that is the point of
 * the separate-table model, and pointing these clicks at /runs/{id} would 404. It shows the
 * same diagnostic material for the fields a batch item carries.
 */

function Row({ label, value, warn }: { label: string; value: React.ReactNode; warn?: boolean }) {
  return (
    <>
      <strong style={{ color: '#9aa0a6', fontWeight: 500 }}>{label}</strong>
      <span style={{ color: warn ? '#f44336' : undefined, wordBreak: 'break-word' }}>
        {value ?? '—'}
      </span>
    </>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '1.2rem' }}>
      <h3 style={{ fontSize: '0.92rem', margin: '0 0 0.5rem' }}>{title}</h3>
      <div style={{ background: theme.bgAlt, border: `1px solid ${theme.border}`, borderRadius: 6, padding: '0.85rem' }}>
        {children}
      </div>
    </div>
  )
}

function secs(v: number | null | undefined) {
  if (v === null || v === undefined) return '—'
  return v < 10 ? `${v.toFixed(2)}s` : `${v.toFixed(1)}s`
}

/** A leg's verdict. NULL is not "passed" — it means no threshold, or not yet evaluated. */
function Leg({ breached, seconds }: { breached: boolean | null; seconds: number | null }) {
  if (breached === null || breached === undefined) {
    return (
      <span>
        {secs(seconds)}{' '}
        <span style={{ color: '#777', fontStyle: 'italic', fontSize: '0.8rem' }}>
          (no verdict — either no SLA is configured for this leg, or it has not completed)
        </span>
      </span>
    )
  }
  return (
    <span style={{ color: breached ? '#f44336' : '#4caf50' }}>
      {secs(seconds)} — {breached ? 'BREACHED its SLA' : 'met its SLA'}
    </span>
  )
}

export default function BatchItemDetail() {
  const { id, itemId } = useParams<{ id: string; itemId: string }>()
  const [item, setItem] = useState<any>(null)
  const [err, setErr] = useState<string | null>(null)

  const load = () => {
    fetch(`/api/batches/${id}/items/${itemId}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then(setItem)
      .catch(e => setErr(e instanceof Error ? e.message : String(e)))
  }

  useEffect(load, [id, itemId])

  if (err) {
    return (
      <div>
        <Link to={`/tactical/${id}`} style={{ color: theme.primary, fontSize: '0.8rem' }}>← Batch report</Link>
        <div style={{ background: '#3a1e1e', border: '1px solid #6a2a2a', borderRadius: 6, padding: '1rem', marginTop: '1rem' }}>
          <p style={{ margin: 0, color: '#ef9a9a' }}>{err}</p>
        </div>
      </div>
    )
  }
  if (!item) return <p style={{ color: '#9aa0a6' }}>Loading…</p>

  const grid = { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.3rem 0.9rem', fontSize: '0.82rem' } as const

  return (
    <div>
      <Link to={`/tactical/${id}`} style={{ color: theme.primary, fontSize: '0.8rem' }}>← Batch report</Link>
      <h2 style={{ margin: '0.5rem 0 0.2rem' }}>
        Fax {item.batch_seq}{item.batch_size ? ` of ${item.batch_size}` : ''}
      </h2>
      <div style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#9aa0a6', marginBottom: '1.2rem' }}>
        {item.run_token}
      </div>

      <Section title="Status & Timing">
        <div style={grid}>
          <Row label="Status" value={item.status} />
          <Row label="Job" value={item.job_name} />
          {/* The SAME labels and the SAME descriptions as run detail, from the same
              registry (G5.14/G5.15). This page used to say "Platform confirmed sent" and
              "Received back" where run detail said "Fax Sent (eFax completed)" and
              "Received" — three labels for two columns, one of them naming a provider
              clock for a value that can be ours. */}
          <TimestampRow
            column="t_outbound_request_started"
            value={item.t_outbound_request_started}
            row={item}
          />
          <TimestampRow column="t_submitted" value={item.t_submitted} row={item} />
          <TimestampRow column="t_fax_sent" value={item.t_fax_sent} row={item} />
          <TimestampRow column="t_received" value={item.t_received} row={item} />
          {item.smtp_notification_received_at && (
            <TimestampRow
              column="smtp_notification_received_at"
              value={item.smtp_notification_received_at}
              row={item}
            />
          )}
          <TimestampRow column="t_quality_checked" value={item.t_quality_checked} row={item} />
        </div>
      </Section>

      <Section title="SLA — an E2E passes only if EVERY configured leg is under its own maximum">
        <div style={grid}>
          <Row
            label="Outbound leg"
            value={<Leg breached={item.outbound_sla_breached} seconds={item.outbound_seconds} />}
          />
          <Row
            label="Inbound leg"
            value={<Leg breached={item.inbound_sla_breached} seconds={item.inbound_seconds} />}
          />
          {/* A DURATION, not a verdict. There is no combined threshold under the per-leg
              rules — the overall window is the derived sum of the legs — so this row must
              not be read as a third pass/fail. A leg breach fails the item even when this
              number sits inside that sum. */}
          <Row label="Combined duration (submit → received)" value={secs(item.combined_seconds)} />
        </div>
      </Section>

      {/* Same shared panel as the run detail page, deliberately. A batch item and a run
          are different rows in different tables but the SAME measurements, and two
          hand-written copies of this is exactly how the per-leg SLA cell ended up
          disagreeing with itself across two pages. Rendered unconditionally: the panel
          explains its own blanks, which is more useful than vanishing. */}
      <Section title="Transmission Measurements">
        <TransmissionMeasurements source={item} />
      </Section>

      {/* WHICH DID THIS FAX ARRIVED FOR, AND ON WHAT AUTHORITY (DID-AUTHORITY-LADDER rule 1).
          Rendered unconditionally, like the measurements panel above and for the same reason: a
          hidden panel leaves a reader unable to tell "nothing was recorded for this fax" from
          "this build has no such panel", and the second reading gets filed as a bug. The panel is
          SHARED with the other detail surface so the two pages cannot describe one row's
          authority differently. */}
      <Section title="Arrival DID attribution">
        <DidAttributionPanel row={item} />
      </Section>

      <Section title="Transmission Quality Score">
        <QualityScorePanel
          score={item.quality_score}
          detail={item.quality_score_detail}
        />
      </Section>

      <Section title="Delivery">
        <div style={grid}>
          {/* The outbound handle under the name ITS OWN provider gives it, from the same
              table every other surface reads (utils/outboundHandle). The column is called
              `efax_fax_id` and is provider-AGNOSTIC: SFax stores its SendFaxQueueId here and
              SRFax its FaxDetailsID. This row read "eFax fax ID" unconditionally, so an
              SRFax FaxDetailsID was displayed to an operator as an eFax id — a number he
              would then quote to the wrong support desk. A row whose provider was never
              recorded says so rather than being labelled with a brand we cannot prove. */}
          <Row label={outboundHandleLabel(item.outbound_provider)} value={item.efax_fax_id} />
          <Row label="Outbound status" value={item.outbound_status} />
          <Row label="SMTP message ID" value={item.smtp_message_id} />
          <Row label="Send attempts" value={item.send_attempts} />
          <Row label="Send error" value={item.send_error || item.quality_detail} warn={!!(item.send_error || item.quality_detail)} />
          <Row label="Quality target sent" value={item.quality_target_sent ? 'yes (an extra page)' : 'no'} />
          <Row label="Received pages" value={item.received_page_count} />
          <Row label="Received size" value={item.received_file_size_bytes ? `${item.received_file_size_bytes.toLocaleString()} bytes` : '—'} />
        </div>
      </Section>

      {item.ocr_text && (
        <Section title="OCR Text (first page)">
          <div style={{ fontSize: '0.78rem', marginBottom: '0.4rem', color: '#9aa0a6' }}>
            Extracted token: <code>{item.ocr_extracted_token || '—'}</code>
          </div>
          <pre style={{ background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 4, padding: '0.6rem', fontSize: '0.72rem', whiteSpace: 'pre-wrap', margin: 0, maxHeight: '18rem', overflow: 'auto' }}>
            {item.ocr_text}
          </pre>
        </Section>
      )}

      {(item.webhook_payload_body || item.imap_headers) && (
        <Section title="Raw inbound payload">
          <pre style={{ background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 4, padding: '0.6rem', fontSize: '0.7rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, maxHeight: '20rem', overflow: 'auto' }}>
            {JSON.stringify(item.webhook_payload_body || item.imap_headers, null, 2)}
          </pre>
        </Section>
      )}
    </div>
  )
}
